import { Trans } from "@lingui/macro";
import { useEffect, useState } from "react";

import { USD_DECIMALS } from "config/factors";
import useV2Stats from "domain/synthetics/stats/useV2Stats";
import { useChainId } from "lib/chains";
import { formatAmountHuman } from "lib/numbers";

import { getFormattedFeesDuration } from "./getFormattedFeesDuration";
import type { ChainStats } from "./useDashboardChainStatsMulticall";

export function StatsCard(_: { statsArbitrum?: ChainStats; statsAvalanche?: ChainStats }) {
  const { chainId } = useChainId();
  const v2Overview = useV2Stats(chainId);
  const [formattedDuration, setFormattedDuration] = useState(() => getFormattedFeesDuration());

  useEffect(() => {
    const interval = setInterval(() => {
      setFormattedDuration(getFormattedFeesDuration());
    }, 1000 * 60);
    return () => clearInterval(interval);
  }, []);

  const totalFeesUsd = v2Overview.totalFees;
  const totalEpochFeesUsd = v2Overview.epochFees;
  const totalVolumeUsd = v2Overview.totalVolume;
  const totalUsers = v2Overview.totalUsers;

  return (
    <div className="App-card !px-24 !pb-34">
      <div className="flex items-center justify-between gap-4 rounded-4 border border-solid border-white-alpha-4 bg-white-alpha-2 px-18 py-9">
        <p className="leading-none text-body-medium font-medium text-white">
          <Trans>Fees {formattedDuration} ago</Trans>
        </p>
        <p className="leading-none text-body-medium whitespace-nowrap font-medium text-primary-alpha-darker">
          {formatAmountHuman(totalEpochFeesUsd, USD_DECIMALS, true, 2)}
        </p>
      </div>
      <div className="mt-22 flex flex-col gap-18">
        <p className="leading-none text-[16px] font-medium text-white">
          <Trans>Total Stats</Trans>
        </p>
        <div className="flex items-center gap-32 sm:gap-35">
          <StatRow label={<Trans>Total Fees</Trans>} value={formatAmountHuman(totalFeesUsd, USD_DECIMALS, true, 2)} />
          <StatRow label={<Trans>Volume</Trans>} value={formatAmountHuman(totalVolumeUsd, USD_DECIMALS, true, 2)} />
          <StatRow label={<Trans>Users</Trans>} value={formatAmountHuman(totalUsers, 0, false, 2)} />
        </div>
      </div>
    </div>
  );
}

const StatRow = ({ label, value }: { label: React.ReactNode; value: string }) => {
  return (
    <div className="flex flex-col gap-10">
      <div className="leading-none text-body-medium font-medium text-gray-lighter">{label}</div>
      <div>
        <p className="leading-none text-body-large whitespace-nowrap font-medium text-white">{value}</p>
      </div>
    </div>
  );
};
